// src/domain/schemas/property.schema.ts
import { z } from "zod";
var AddressSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  country: z.string().default("US")
});
var CurrencySchema = z.enum(["USD", "EUR", "GBP", "CAD", "AUD"]);
var PropertyRecordSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1, "Property name is required").max(200),
  address: AddressSchema.default({}),
  tags: z.array(z.string()).default([]),
  currency: CurrencySchema.default("USD"),
  timezone: z.string().default("America/New_York"),
  notes: z.string().optional(),
  createdAt: z.date(),
  updatedAt: z.date()
});
function createPropertyRecord(partial) {
  const now = /* @__PURE__ */ new Date();
  return PropertyRecordSchema.parse({
    id: crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
    ...partial
  });
}

// src/domain/schemas/deal-input.schema.ts
import { z as z2 } from "zod";
var PropertyInfoSchema = z2.object({
  /** Property name/title */
  name: z2.string().default(""),
  /** Full address or single-line address */
  address: z2.string().default(""),
  /** City */
  city: z2.string().default(""),
  /** State/Province */
  state: z2.string().default(""),
  /** ZIP/Postal code */
  zipCode: z2.string().default("")
});
var PurchaseSchema = z2.object({
  /** Purchase price of the property */
  purchasePrice: z2.number().positive("Purchase price must be positive"),
  /** After Repair Value (ARV) - estimated market value after renovations */
  afterRepairValue: z2.number().nonnegative().optional(),
  /** Closing costs (title, escrow, legal, etc.) */
  closingCosts: z2.number().nonnegative().default(0),
  /** Rehab/repair costs */
  rehabCosts: z2.number().nonnegative().default(0),
  /** Furnishing costs (for furnished rentals) */
  furnishingCosts: z2.number().nonnegative().default(0),
  /** Acquisition date */
  acquisitionDate: z2.date().optional()
});
var LoanTypeSchema = z2.enum([
  "conventional",
  "fha",
  "va",
  "commercial",
  "portfolio",
  "hard_money"
]);
var FinancingSchema = z2.object({
  /** Down payment as a percentage (0-100) */
  downPaymentPercent: z2.number().min(0).max(100).default(20),
  /** Loan amount (calculated if not provided) */
  loanAmount: z2.number().nonnegative().optional(),
  /** Annual interest rate as a percentage (e.g., 7.5 for 7.5%) */
  interestRate: z2.number().min(0).max(100).default(7),
  /** Loan term in years */
  loanTermYears: z2.number().int().positive().default(30),
  /** Amortization period in years (can differ from loan term for balloon loans) */
  amortizationYears: z2.number().int().positive().optional(),
  /** Type of loan */
  loanType: LoanTypeSchema.default("conventional"),
  /** Points paid at closing (as percentage of loan amount) */
  pointsPaid: z2.number().nonnegative().default(0),
  /** Other lender fees */
  lenderFees: z2.number().nonnegative().default(0)
});
var IncomeSchema = z2.object({
  /** Monthly rental income */
  monthlyRent: z2.number().nonnegative().default(0),
  /** Other monthly income (parking, laundry, storage, etc.) */
  otherMonthlyIncome: z2.number().nonnegative().default(0),
  /** Vacancy rate as percentage (0-100) */
  vacancyRate: z2.number().min(0).max(100).default(5),
  /** Annual rent growth rate as percentage */
  annualRentGrowth: z2.number().min(-50).max(100).default(2)
});
var ExpensesSchema = z2.object({
  /** Annual property tax */
  propertyTax: z2.number().nonnegative().default(0),
  /** Annual insurance premium */
  insurance: z2.number().nonnegative().default(0),
  /** Monthly HOA fees */
  hoaMonthly: z2.number().nonnegative().default(0),
  /** Annual maintenance (or use maintenancePercent) */
  maintenance: z2.number().nonnegative().optional(),
  /** Maintenance as percentage of gross rent (5-15% typical) */
  maintenancePercent: z2.number().min(0).max(100).default(5),
  /** Annual CapEx reserve (or use capexPercent) */
  capex: z2.number().nonnegative().optional(),
  /** CapEx as percentage of gross rent (5-10% typical) */
  capexPercent: z2.number().min(0).max(100).default(5),
  /** Property management (or use propertyManagementPercent) */
  propertyManagement: z2.number().nonnegative().optional(),
  /** Property management as percentage of effective gross income (8-12% typical) */
  propertyManagementPercent: z2.number().min(0).max(100).default(8),
  /** Monthly utilities (if landlord pays) */
  utilitiesMonthly: z2.number().nonnegative().default(0),
  /** Annual leasing/turnover costs */
  leasingCosts: z2.number().nonnegative().default(0),
  /** Other annual expenses */
  otherExpenses: z2.number().nonnegative().default(0),
  /** Annual expense growth rate as percentage */
  annualExpenseGrowth: z2.number().min(-50).max(100).default(2)
});
var AppreciationExitSchema = z2.object({
  /** Annual property appreciation rate as percentage */
  annualAppreciation: z2.number().min(-50).max(100).default(3),
  /** Holding period in years */
  holdingPeriodYears: z2.number().int().min(1).max(50).default(30),
  /** Selling costs as percentage of sale price (agent fees, closing, etc.) */
  sellingCostsPercent: z2.number().min(0).max(30).default(6),
  /** Override sale price (if known, otherwise calculated from appreciation) */
  salePriceOverride: z2.number().positive().optional()
});
var TaxSettingsSchema = z2.object({
  /** Marginal income tax rate as percentage */
  marginalTaxRate: z2.number().min(0).max(100).optional(),
  /** State income tax rate as percentage */
  stateTaxRate: z2.number().min(0).max(100).optional(),
  /** Capital gains tax rate as percentage */
  capitalGainsTaxRate: z2.number().min(0).max(100).optional(),
  /** Enable depreciation calculations */
  depreciationEnabled: z2.boolean().default(false),
  /** Depreciation period in years (27.5 for residential, 39 for commercial) */
  depreciationYears: z2.number().min(1).max(50).default(27.5),
  /** Land value as percentage of purchase price (not depreciable) */
  landValuePercent: z2.number().min(0).max(100).default(20)
}).optional();
var DealInputSchema = z2.object({
  /** Reference to the property record */
  propertyId: z2.string().uuid().optional(),
  /** Schema version for migrations */
  schemaVersion: z2.number().int().default(1),
  /** Property info (name, address) */
  propertyInfo: PropertyInfoSchema.default({}),
  /** Purchase details */
  purchase: PurchaseSchema,
  /** Financing details */
  financing: FinancingSchema,
  /** Income details */
  income: IncomeSchema,
  /** Expense details */
  expenses: ExpensesSchema,
  /** Appreciation and exit strategy */
  appreciationExit: AppreciationExitSchema,
  /** Tax settings (optional) */
  taxes: TaxSettingsSchema
});
function createDefaultDealInput() {
  return DealInputSchema.parse({
    propertyInfo: {
      name: "",
      address: "",
      city: "",
      state: "",
      zipCode: ""
    },
    purchase: {
      purchasePrice: 3e5,
      closingCosts: 6e3,
      rehabCosts: 0
    },
    financing: {
      downPaymentPercent: 20,
      interestRate: 7,
      loanTermYears: 30
    },
    income: {
      monthlyRent: 2e3,
      vacancyRate: 5,
      annualRentGrowth: 2
    },
    expenses: {
      propertyTax: 3600,
      insurance: 1200,
      maintenancePercent: 5,
      capexPercent: 5,
      propertyManagementPercent: 8
    },
    appreciationExit: {
      annualAppreciation: 3,
      holdingPeriodYears: 10,
      sellingCostsPercent: 6
    }
  });
}

// src/domain/schemas/analysis-result.schema.ts
import { z as z3 } from "zod";
var SummaryMetricsSchema = z3.object({
  // Core income metrics
  /** Annual Gross Potential Income */
  grossPotentialIncome: z3.number(),
  /** Annual Effective Gross Income (after vacancy) */
  effectiveGrossIncome: z3.number(),
  /** Annual Operating Expenses */
  totalOperatingExpenses: z3.number(),
  /** Net Operating Income (EGI - OpEx) */
  noi: z3.number(),
  /** Operating Expense Ratio (OpEx / EGI) */
  oer: z3.number(),
  // Purchase metrics
  /** Original purchase price of the property */
  purchasePrice: z3.number(),
  /** Total cash required at closing */
  totalCashRequired: z3.number(),
  /** Loan amount */
  loanAmount: z3.number(),
  /** Monthly debt service (P&I) */
  monthlyDebtService: z3.number(),
  /** Annual debt service */
  annualDebtService: z3.number(),
  // Return metrics
  /** Cap Rate (NOI / Purchase Price) */
  capRate: z3.number(),
  /** Cash-on-Cash Return (Annual Cash Flow / Total Cash Invested) */
  cashOnCash: z3.number(),
  /** Gross Rent Multiplier (Purchase Price / Annual Gross Rent) */
  grm: z3.number(),
  /** Debt Service Coverage Ratio (NOI / Annual Debt Service) */
  dscr: z3.number(),
  // Year 1 cash flow
  /** Monthly cash flow (NOI/12 - Monthly Debt Service) */
  monthlyCashFlow: z3.number(),
  /** Annual cash flow */
  annualCashFlow: z3.number(),
  // Advanced return metrics (calculated in Phase 2)
  /** Internal Rate of Return - leveraged */
  irrLeveraged: z3.number().nullable(),
  /** Internal Rate of Return - unleveraged */
  irrUnleveraged: z3.number().nullable(),
  /** Net Present Value at given discount rate */
  npv: z3.number(),
  /** Discount rate used for NPV calculation */
  npvDiscountRate: z3.number(),
  /** Equity Multiple (Total Returns / Total Cash Invested) */
  equityMultiple: z3.number(),
  /** Total return over holding period (dollars) */
  totalReturn: z3.number(),
  /** Total return as percentage of investment */
  totalReturnPercent: z3.number(),
  /** Average annual return (simple, not compounded) */
  averageAnnualReturn: z3.number(),
  /** Break-even occupancy rate (as decimal) */
  breakEvenOccupancy: z3.number(),
  /** Years to recoup initial investment from cashflow (null if never) */
  paybackPeriodYears: z3.number().nullable(),
  /** Rent to price ratio (1% rule check) */
  rentToPrice: z3.number(),
  /** Profitability Index (PV of future cashflows / initial investment) */
  profitabilityIndex: z3.number(),
  // Tax-aware metrics (optional)
  /** After-tax cash-on-cash return */
  afterTaxCashOnCash: z3.number().optional(),
  /** After-tax IRR */
  afterTaxIrr: z3.number().optional()
});
var MonthlyCashflowSchema = z3.object({
  /** Month number (1-indexed) */
  month: z3.number().int().positive(),
  /** Year number (1-indexed) */
  year: z3.number().int().positive(),
  /** Date of this month */
  date: z3.date().optional(),
  /** Gross scheduled rent */
  grossRent: z3.number(),
  /** Other income */
  otherIncome: z3.number(),
  /** Vacancy loss */
  vacancyLoss: z3.number(),
  /** Effective gross income */
  effectiveGrossIncome: z3.number(),
  /** Total operating expenses */
  operatingExpenses: z3.number(),
  /** Net operating income */
  noi: z3.number(),
  /** Debt service (P&I) */
  debtService: z3.number(),
  /** Cash flow (NOI - Debt Service) */
  cashFlow: z3.number(),
  /** Cumulative cash flow from start */
  cumulativeCashFlow: z3.number()
});
var AnnualCashflowSchema = z3.object({
  /** Year number (1-indexed) */
  year: z3.number().int().positive(),
  /** Total gross rent for year */
  grossRent: z3.number(),
  /** Total other income */
  otherIncome: z3.number(),
  /** Total vacancy loss */
  vacancyLoss: z3.number(),
  /** Effective gross income */
  effectiveGrossIncome: z3.number(),
  /** Total operating expenses */
  operatingExpenses: z3.number(),
  /** Net operating income */
  noi: z3.number(),
  /** Total debt service for year */
  debtService: z3.number(),
  /** Net cash flow */
  cashFlow: z3.number(),
  /** Cumulative cash flow */
  cumulativeCashFlow: z3.number(),
  /** Property value at year end */
  propertyValue: z3.number(),
  /** Loan balance at year end */
  loanBalance: z3.number(),
  /** Total equity (property value - loan balance) */
  equity: z3.number(),
  /** Principal paid during year */
  principalPaid: z3.number(),
  /** Interest paid during year */
  interestPaid: z3.number()
});
var AmortizationEntrySchema = z3.object({
  /** Payment number (1-indexed) */
  paymentNumber: z3.number().int().positive(),
  /** Month number */
  month: z3.number().int().positive(),
  /** Year number */
  year: z3.number().int().positive(),
  /** Date of payment */
  date: z3.date().optional(),
  /** Total payment amount */
  payment: z3.number(),
  /** Principal portion */
  principal: z3.number(),
  /** Interest portion */
  interest: z3.number(),
  /** Remaining balance after payment */
  balance: z3.number(),
  /** Cumulative principal paid */
  cumulativePrincipal: z3.number(),
  /** Cumulative interest paid */
  cumulativeInterest: z3.number()
});
var EquityTimelineEntrySchema = z3.object({
  /** Month number (1-indexed) */
  month: z3.number().int().positive(),
  /** Year number (1-indexed) */
  year: z3.number().int().positive(),
  /** Property market value */
  propertyValue: z3.number(),
  /** Remaining loan balance */
  loanBalance: z3.number(),
  /** Total equity (value - balance) */
  equity: z3.number(),
  /** Equity from appreciation */
  appreciationEquity: z3.number(),
  /** Equity from principal paydown */
  principalEquity: z3.number(),
  /** Initial equity (down payment + closing) */
  initialEquity: z3.number()
});
var WarningSeveritySchema = z3.enum(["info", "warning", "error"]);
var WarningSchema = z3.object({
  /** Warning code for programmatic handling */
  code: z3.string(),
  /** Severity level */
  severity: WarningSeveritySchema,
  /** Human-readable message */
  message: z3.string(),
  /** Related input field (if applicable) */
  field: z3.string().optional(),
  /** Suggested fix */
  suggestion: z3.string().optional()
});
var ExplainabilityStepSchema = z3.object({
  /** Name of the metric */
  metricName: z3.string(),
  /** LaTeX or text formula */
  formula: z3.string(),
  /** Input values used */
  inputs: z3.record(z3.string(), z3.number()),
  /** Calculated result */
  result: z3.number(),
  /** Human-readable description */
  description: z3.string()
});
var ExpenseBreakdownSchema = z3.object({
  propertyTax: z3.number(),
  insurance: z3.number(),
  hoa: z3.number(),
  maintenance: z3.number(),
  capex: z3.number(),
  propertyManagement: z3.number(),
  utilities: z3.number(),
  leasing: z3.number(),
  other: z3.number(),
  total: z3.number()
});
var IncomeBreakdownSchema = z3.object({
  grossRent: z3.number(),
  otherIncome: z3.number(),
  vacancyLoss: z3.number(),
  effectiveGrossIncome: z3.number()
});
var AnalysisResultSchema = z3.object({
  /** Summary metrics */
  summaryMetrics: SummaryMetricsSchema,
  /** Monthly cashflow projections */
  monthlyCashflows: z3.array(MonthlyCashflowSchema),
  /** Annual cashflow summaries */
  annualCashflows: z3.array(AnnualCashflowSchema),
  /** Loan amortization schedule */
  amortizationSchedule: z3.array(AmortizationEntrySchema),
  /** Equity growth timeline */
  equityTimeline: z3.array(EquityTimelineEntrySchema),
  /** Expense breakdown */
  expenseBreakdown: ExpenseBreakdownSchema,
  /** Income breakdown */
  incomeBreakdown: IncomeBreakdownSchema,
  /** Analysis warnings */
  warnings: z3.array(WarningSchema),
  /** Calculation explanations */
  explainability: z3.array(ExplainabilityStepSchema),
  /** Timestamp of analysis */
  computedAt: z3.date(),
  /** Time taken to compute (ms) */
  computeTimeMs: z3.number()
});

// src/domain/schemas/scenario.schema.ts
import { z as z4 } from "zod";
var ScenarioSchema = z4.object({
  /** Unique identifier */
  id: z4.string().uuid(),
  /** Reference to the property */
  propertyId: z4.string().uuid(),
  /** Scenario name (e.g., "Optimistic", "Conservative", "High Vacancy") */
  name: z4.string().min(1).max(100),
  /** Description of what this scenario represents */
  description: z4.string().optional(),
  /** Partial overrides to apply to the base deal input */
  overrides: DealInputSchema.deepPartial(),
  /** Whether this is the base/default scenario */
  isBase: z4.boolean().default(false),
  /** Order for display */
  sortOrder: z4.number().int().default(0),
  /** Creation timestamp */
  createdAt: z4.date(),
  /** Last update timestamp */
  updatedAt: z4.date()
});
var ScenarioTemplates = {
  optimistic: {
    name: "Optimistic",
    description: "Best-case scenario with higher rents and appreciation",
    overrides: {
      income: {
        vacancyRate: 3,
        annualRentGrowth: 4
      },
      appreciationExit: {
        annualAppreciation: 5
      }
    }
  },
  conservative: {
    name: "Conservative",
    description: "Cautious scenario with higher vacancy and expenses",
    overrides: {
      income: {
        vacancyRate: 10,
        annualRentGrowth: 1
      },
      expenses: {
        maintenancePercent: 8,
        capexPercent: 8
      },
      appreciationExit: {
        annualAppreciation: 1
      }
    }
  },
  worstCase: {
    name: "Worst Case",
    description: "Stress test with high vacancy and no appreciation",
    overrides: {
      income: {
        vacancyRate: 15,
        annualRentGrowth: 0
      },
      expenses: {
        maintenancePercent: 10,
        capexPercent: 10
      },
      appreciationExit: {
        annualAppreciation: 0
      }
    }
  },
  highInterestRate: {
    name: "Higher Interest Rate",
    description: "Scenario with 2% higher interest rate",
    overrides: {
      financing: {
        interestRate: 9
        // Will be adjusted relative to base
      }
    }
  }
};
function createScenario(propertyId, partial) {
  const now = /* @__PURE__ */ new Date();
  return ScenarioSchema.parse({
    id: crypto.randomUUID(),
    propertyId,
    createdAt: now,
    updatedAt: now,
    overrides: {},
    ...partial
  });
}
function createScenarioFromTemplate(propertyId, templateKey) {
  const template = ScenarioTemplates[templateKey];
  return createScenario(propertyId, {
    name: template.name,
    description: template.description,
    overrides: template.overrides
  });
}

// src/calculations/metrics/noi.ts
import { round, fromPercent } from "@roi/utils";
function calculateIncomeBreakdown(input) {
  const annualGrossRent = input.income.monthlyRent * 12;
  const annualOtherIncome = input.income.otherMonthlyIncome * 12;
  const grossPotentialIncome = annualGrossRent + annualOtherIncome;
  const vacancyRate = fromPercent(input.income.vacancyRate);
  const vacancyLoss = round(grossPotentialIncome * vacancyRate, 2);
  const effectiveGrossIncome = round(grossPotentialIncome - vacancyLoss, 2);
  return {
    grossRent: annualGrossRent,
    otherIncome: annualOtherIncome,
    vacancyLoss,
    effectiveGrossIncome
  };
}
function calculateExpenseBreakdown(input, effectiveGrossIncome) {
  const grossRent = input.income.monthlyRent * 12;
  const propertyTax = input.expenses.propertyTax;
  const insurance = input.expenses.insurance;
  const hoa = input.expenses.hoaMonthly * 12;
  const maintenance = input.expenses.maintenance !== void 0 ? input.expenses.maintenance : round(grossRent * fromPercent(input.expenses.maintenancePercent), 2);
  const capex = input.expenses.capex !== void 0 ? input.expenses.capex : round(grossRent * fromPercent(input.expenses.capexPercent), 2);
  const propertyManagement = input.expenses.propertyManagement !== void 0 ? input.expenses.propertyManagement : round(effectiveGrossIncome * fromPercent(input.expenses.propertyManagementPercent), 2);
  const utilities = input.expenses.utilitiesMonthly * 12;
  const leasing = input.expenses.leasingCosts;
  const other = input.expenses.otherExpenses;
  const total = round(
    propertyTax + insurance + hoa + maintenance + capex + propertyManagement + utilities + leasing + other,
    2
  );
  return {
    propertyTax,
    insurance,
    hoa,
    maintenance,
    capex,
    propertyManagement,
    utilities,
    leasing,
    other,
    total
  };
}
function calculateNOI(effectiveGrossIncome, totalOperatingExpenses) {
  return round(effectiveGrossIncome - totalOperatingExpenses, 2);
}
function calculateOER(totalOperatingExpenses, effectiveGrossIncome) {
  if (effectiveGrossIncome === 0) return 0;
  return round(totalOperatingExpenses / effectiveGrossIncome, 4);
}

// src/calculations/metrics/returns.ts
import { round as round2, fromPercent as fromPercent2 } from "@roi/utils";
function calculateTotalCashRequired(input) {
  const downPayment = input.purchase.purchasePrice * fromPercent2(input.financing.downPaymentPercent);
  const closingCosts = input.purchase.closingCosts;
  const rehabCosts = input.purchase.rehabCosts;
  const furnishingCosts = input.purchase.furnishingCosts;
  const loanAmount = calculateLoanAmount(input);
  const pointsCost = loanAmount * fromPercent2(input.financing.pointsPaid);
  const lenderFees = input.financing.lenderFees;
  return round2(
    downPayment + closingCosts + rehabCosts + furnishingCosts + pointsCost + lenderFees,
    2
  );
}
function calculateLoanAmount(input) {
  if (input.financing.loanAmount !== void 0) {
    return input.financing.loanAmount;
  }
  const downPayment = input.purchase.purchasePrice * fromPercent2(input.financing.downPaymentPercent);
  return round2(input.purchase.purchasePrice - downPayment, 2);
}
function calculateCapRate(noi, purchasePrice) {
  if (purchasePrice === 0) return 0;
  return round2(noi / purchasePrice, 4);
}
function calculateCashOnCash(annualCashFlow, totalCashInvested) {
  if (totalCashInvested === 0) return 0;
  return round2(annualCashFlow / totalCashInvested, 4);
}
function calculateGRM(purchasePrice, annualGrossRent) {
  if (annualGrossRent === 0) return 0;
  return round2(purchasePrice / annualGrossRent, 2);
}
function calculateDSCR(noi, annualDebtService) {
  if (annualDebtService === 0) return Infinity;
  return round2(noi / annualDebtService, 2);
}

// src/calculations/metrics/irr.ts
import { round as round3 } from "@roi/utils";
var MAX_ITERATIONS = 100;
var TOLERANCE = 1e-7;
var DEFAULT_GUESS = 0.1;
function calculateNPVForRate(cashflows, rate) {
  return cashflows.reduce((npv, cf, t) => {
    return npv + cf / Math.pow(1 + rate, t);
  }, 0);
}
function calculateNPVDerivative(cashflows, rate) {
  return cashflows.reduce((derivative, cf, t) => {
    if (t === 0) return derivative;
    return derivative - t * cf / Math.pow(1 + rate, t + 1);
  }, 0);
}
function hasSignChange(cashflows) {
  let positiveFound = false;
  let negativeFound = false;
  for (const cf of cashflows) {
    if (cf > 0) positiveFound = true;
    if (cf < 0) negativeFound = true;
    if (positiveFound && negativeFound) return true;
  }
  return false;
}
function calculateIRR(cashflows, guess = DEFAULT_GUESS) {
  if (!cashflows || cashflows.length < 2) {
    return null;
  }
  if (!hasSignChange(cashflows)) {
    return null;
  }
  let rate = guess;
  for (let i = 0; i < MAX_ITERATIONS; i++) {
    const npv = calculateNPVForRate(cashflows, rate);
    const derivative = calculateNPVDerivative(cashflows, rate);
    if (Math.abs(derivative) < 1e-10) {
      return calculateIRRBisection(cashflows);
    }
    const newRate = rate - npv / derivative;
    if (Math.abs(newRate - rate) < TOLERANCE) {
      if (newRate > -1 && newRate < 10) {
        return round3(newRate, 6);
      }
      return null;
    }
    rate = newRate;
    if (rate < -0.99) rate = -0.99;
    if (rate > 10) rate = 10;
  }
  return calculateIRRBisection(cashflows);
}
function calculateIRRBisection(cashflows) {
  let low = -0.99;
  let high = 10;
  const npvLow = calculateNPVForRate(cashflows, low);
  const npvHigh = calculateNPVForRate(cashflows, high);
  if (npvLow * npvHigh > 0) {
    return null;
  }
  for (let i = 0; i < MAX_ITERATIONS; i++) {
    const mid = (low + high) / 2;
    const npvMid = calculateNPVForRate(cashflows, mid);
    if (Math.abs(npvMid) < TOLERANCE || (high - low) / 2 < TOLERANCE) {
      return round3(mid, 6);
    }
    if (npvMid * calculateNPVForRate(cashflows, low) < 0) {
      high = mid;
    } else {
      low = mid;
    }
  }
  return null;
}
function calculateLeveragedIRR(initialInvestment, annualCashflows, saleProceeds) {
  const cashflows = [-initialInvestment, ...annualCashflows];
  if (cashflows.length > 1) {
    cashflows[cashflows.length - 1] += saleProceeds;
  }
  return calculateIRR(cashflows);
}
function calculateUnleveragedIRR(purchasePrice, annualNOI, salePrice, sellingCosts) {
  const cashflows = [-purchasePrice, ...annualNOI];
  if (cashflows.length > 1) {
    cashflows[cashflows.length - 1] += salePrice - sellingCosts;
  }
  return calculateIRR(cashflows);
}
function calculateMIRR(cashflows, financeRate, reinvestRate) {
  if (!cashflows || cashflows.length < 2) {
    return null;
  }
  const n = cashflows.length - 1;
  let pvNegative = 0;
  let fvPositive = 0;
  for (let t = 0; t <= n; t++) {
    const cf = cashflows[t];
    if (cf < 0) {
      pvNegative += cf / Math.pow(1 + financeRate, t);
    } else {
      fvPositive += cf * Math.pow(1 + reinvestRate, n - t);
    }
  }
  if (pvNegative >= 0 || fvPositive <= 0) {
    return null;
  }
  const mirr = Math.pow(fvPositive / Math.abs(pvNegative), 1 / n) - 1;
  return round3(mirr, 6);
}

// src/calculations/metrics/npv.ts
import { round as round4 } from "@roi/utils";
function calculateNPV(cashflows, discountRate) {
  if (!cashflows || cashflows.length === 0) {
    return 0;
  }
  const npv = cashflows.reduce((pv, cf, t) => {
    return pv + cf / Math.pow(1 + discountRate, t);
  }, 0);
  return round4(npv, 2);
}
function calculateInvestmentNPV(initialInvestment, annualCashflows, saleProceeds, discountRate) {
  const cashflows = [-initialInvestment, ...annualCashflows];
  if (cashflows.length > 1) {
    cashflows[cashflows.length - 1] += saleProceeds;
  }
  return calculateNPV(cashflows, discountRate);
}
function calculatePresentValue(futureValue, discountRate, periods) {
  return round4(futureValue / Math.pow(1 + discountRate, periods), 2);
}
function calculateFutureValue(presentValue, rate, periods) {
  return round4(presentValue * Math.pow(1 + rate, periods), 2);
}
function calculateAnnuityPV(payment, discountRate, periods) {
  if (discountRate === 0) {
    return round4(payment * periods, 2);
  }
  const pv = payment * ((1 - Math.pow(1 + discountRate, -periods)) / discountRate);
  return round4(pv, 2);
}
function calculateProfitabilityIndex(cashflows, discountRate) {
  if (!cashflows || cashflows.length < 2) {
    return 0;
  }
  const initialInvestment = Math.abs(cashflows[0]);
  if (initialInvestment === 0) {
    return 0;
  }
  const futureCashflows = cashflows.slice(1);
  const pvFuture = futureCashflows.reduce((pv, cf, t) => {
    return pv + cf / Math.pow(1 + discountRate, t + 1);
  }, 0);
  return round4(pvFuture / initialInvestment, 4);
}
function calculateRequiredDiscountRate(cashflows, targetNPV = 0) {
  const adjustedCashflows = [...cashflows];
  adjustedCashflows[0] -= targetNPV;
  let low = -0.5;
  let high = 2;
  const tolerance = 1e-6;
  const maxIterations = 100;
  for (let i = 0; i < maxIterations; i++) {
    const mid = (low + high) / 2;
    const npv = calculateNPV(adjustedCashflows, mid);
    if (Math.abs(npv) < tolerance) {
      return round4(mid, 6);
    }
    if (npv > 0) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return null;
}

// src/calculations/metrics/advanced-returns.ts
import { round as round5 } from "@roi/utils";
function calculateEquityMultiple(totalCashInvested, totalCashReturned) {
  if (totalCashInvested <= 0) return 0;
  return round5(totalCashReturned / totalCashInvested, 2);
}
function calculateEquityMultipleFromCashflows(initialInvestment, annualCashflows, saleProceeds) {
  const totalCashflows = annualCashflows.reduce((sum, cf) => sum + cf, 0);
  const totalReturned = totalCashflows + saleProceeds;
  return calculateEquityMultiple(initialInvestment, totalReturned);
}
function calculateTotalReturn(initialInvestment, annualCashflows, saleProceeds) {
  const totalCashflows = annualCashflows.reduce((sum, cf) => sum + cf, 0);
  return round5(totalCashflows + saleProceeds - initialInvestment, 2);
}
function calculateTotalReturnPercent(initialInvestment, totalReturn) {
  if (initialInvestment <= 0) return 0;
  return round5(totalReturn / initialInvestment, 4);
}
function calculateAverageAnnualReturn(totalReturnPercent, holdingPeriodYears) {
  if (holdingPeriodYears <= 0) return 0;
  return round5(totalReturnPercent / holdingPeriodYears, 4);
}
function calculateBreakEvenOccupancy(grossPotentialRent, operatingExpenses, debtService) {
  if (grossPotentialRent <= 0) return 1;
  const totalRequiredIncome = operatingExpenses + debtService;
  const breakEven = totalRequiredIncome / grossPotentialRent;
  return round5(Math.min(breakEven, 1), 4);
}
function calculateBreakEvenRent(operatingExpenses, debtService, vacancyRate) {
  const effectiveRentRate = 1 - vacancyRate;
  if (effectiveRentRate <= 0) return Infinity;
  const requiredGrossRent = (operatingExpenses + debtService) / effectiveRentRate;
  return round5(requiredGrossRent, 2);
}
function calculatePaybackPeriod(initialInvestment, annualCashflows) {
  if (initialInvestment <= 0) return 0;
  let cumulativeCashflow = 0;
  for (let year = 0; year < annualCashflows.length; year++) {
    const previousCumulative = cumulativeCashflow;
    cumulativeCashflow += annualCashflows[year];
    if (cumulativeCashflow >= initialInvestment) {
      const remaining = initialInvestment - previousCumulative;
      const yearlyContribution = annualCashflows[year];
      if (yearlyContribution > 0) {
        const fractionalYear = remaining / yearlyContribution;
        return round5(year + fractionalYear, 2);
      }
      return year + 1;
    }
  }
  return null;
}
function calculateDiscountedPaybackPeriod(initialInvestment, annualCashflows, discountRate) {
  if (initialInvestment <= 0) return 0;
  let cumulativeDiscountedCashflow = 0;
  for (let year = 0; year < annualCashflows.length; year++) {
    const previousCumulative = cumulativeDiscountedCashflow;
    const discountedCashflow = annualCashflows[year] / Math.pow(1 + discountRate, year + 1);
    cumulativeDiscountedCashflow += discountedCashflow;
    if (cumulativeDiscountedCashflow >= initialInvestment) {
      const remaining = initialInvestment - previousCumulative;
      if (discountedCashflow > 0) {
        const fractionalYear = remaining / discountedCashflow;
        return round5(year + fractionalYear, 2);
      }
      return year + 1;
    }
  }
  return null;
}
function calculateReturnOnEquity(annualCashflow, currentEquity) {
  if (currentEquity <= 0) return 0;
  return round5(annualCashflow / currentEquity, 4);
}
function calculateRentToPrice(monthlyRent, purchasePrice) {
  if (purchasePrice <= 0) return 0;
  return round5(monthlyRent / purchasePrice, 4);
}
function meetsOnePercentRule(monthlyRent, purchasePrice) {
  return calculateRentToPrice(monthlyRent, purchasePrice) >= 0.01;
}
function estimateExpensesFiftyPercent(grossMonthlyRent) {
  return round5(grossMonthlyRent * 0.5, 2);
}
function calculateMaxPurchasePrice(afterRepairValue, repairCosts) {
  return round5(afterRepairValue * 0.7 - repairCosts, 2);
}

// src/calculations/projections/amortization.ts
import { round as round6, fromPercent as fromPercent3, calculateMonthlyPayment } from "@roi/utils";
function generateAmortizationSchedule(input, loanAmount, startDate) {
  const monthlyRate = fromPercent3(input.financing.interestRate) / 12;
  const termMonths = input.financing.loanTermYears * 12;
  const amortizationMonths = input.financing.amortizationYears ? input.financing.amortizationYears * 12 : termMonths;
  const monthlyPayment = calculateMonthlyPayment(loanAmount, fromPercent3(input.financing.interestRate), amortizationMonths);
  const schedule = [];
  let balance = loanAmount;
  let cumulativePrincipal = 0;
  let cumulativeInterest = 0;
  const baseDate = startDate || /* @__PURE__ */ new Date();
  for (let i = 1; i <= termMonths && balance > 0; i++) {
    const interestPayment = round6(balance * monthlyRate, 2);
    let principalPayment = round6(monthlyPayment - interestPayment, 2);
    if (principalPayment > balance) {
      principalPayment = balance;
    }
    const newBalance = round6(balance - principalPayment, 2);
    cumulativePrincipal = round6(cumulativePrincipal + principalPayment, 2);
    cumulativeInterest = round6(cumulativeInterest + interestPayment, 2);
    const paymentDate = new Date(baseDate);
    paymentDate.setMonth(paymentDate.getMonth() + i);
    schedule.push({
      paymentNumber: i,
      month: i,
      year: Math.ceil(i / 12),
      date: paymentDate,
      payment: round6(principalPayment + interestPayment, 2),
      principal: principalPayment,
      interest: interestPayment,
      balance: newBalance,
      cumulativePrincipal,
      cumulativeInterest
    });
    balance = newBalance;
  }
  return {
    monthlyPayment: round6(monthlyPayment, 2),
    totalPayments: round6(schedule.reduce((sum, entry) => sum + entry.payment, 0), 2),
    totalInterest: cumulativeInterest,
    totalPrincipal: cumulativePrincipal,
    schedule
  };
}
function getLoanBalanceAtMonth(schedule, month) {
  if (month <= 0) return schedule[0]?.balance || 0;
  if (month > schedule.length) return 0;
  return schedule[month - 1].balance;
}
function getPrincipalPaidByMonth(schedule, month) {
  if (month <= 0) return 0;
  if (month > schedule.length) month = schedule.length;
  return schedule[month - 1].cumulativePrincipal;
}
function getInterestPaidByMonth(schedule, month) {
  if (month <= 0) return 0;
  if (month > schedule.length) month = schedule.length;
  return schedule[month - 1].cumulativeInterest;
}
function getAnnualDebtBreakdown(schedule, year) {
  const startMonth = (year - 1) * 12 + 1;
  const endMonth = year * 12;
  let principal = 0;
  let interest = 0;
  for (let month = startMonth; month <= endMonth && month <= schedule.length; month++) {
    const entry = schedule[month - 1];
    principal += entry.principal;
    interest += entry.interest;
  }
  return {
    principal: round6(principal, 2),
    interest: round6(interest, 2),
    total: round6(principal + interest, 2)
  };
}

// src/calculations/projections/cashflows.ts
import { round as round7, fromPercent as fromPercent4, compoundGrowth } from "@roi/utils";
function generateMonthlyCashflows(input, _incomeBreakdown, expenseBreakdown, amortizationSchedule, startDate) {
  const totalMonths = input.appreciationExit.holdingPeriodYears * 12;
  const cashflows = [];
  let cumulativeCashFlow = 0;
  const baseDate = startDate || /* @__PURE__ */ new Date();
  const rentGrowthRate = fromPercent4(input.income.annualRentGrowth);
  const expenseGrowthRate = fromPercent4(input.expenses.annualExpenseGrowth);
  const vacancyRate = fromPercent4(input.income.vacancyRate);
  const baseMonthlyRent = input.income.monthlyRent;
  const baseMonthlyOtherIncome = input.income.otherMonthlyIncome;
  const baseMonthlyExpenses = expenseBreakdown.total / 12;
  for (let month = 1; month <= totalMonths; month++) {
    const year = Math.ceil(month / 12);
    const yearsElapsed = year - 1;
    const growthMultiplierIncome = yearsElapsed > 0 ? compoundGrowth(1, rentGrowthRate, yearsElapsed) : 1;
    const growthMultiplierExpense = yearsElapsed > 0 ? compoundGrowth(1, expenseGrowthRate, yearsElapsed) : 1;
    const monthlyRent = round7(baseMonthlyRent * growthMultiplierIncome, 2);
    const monthlyOtherIncome = round7(baseMonthlyOtherIncome * growthMultiplierIncome, 2);
    const grossIncome = monthlyRent + monthlyOtherIncome;
    const vacancyLoss = round7(grossIncome * vacancyRate, 2);
    const effectiveGrossIncome = round7(grossIncome - vacancyLoss, 2);
    const operatingExpenses = round7(baseMonthlyExpenses * growthMultiplierExpense, 2);
    const noi = round7(effectiveGrossIncome - operatingExpenses, 2);
    const debtService = month <= amortizationSchedule.length ? amortizationSchedule[month - 1].payment : 0;
    const cashFlow = round7(noi - debtService, 2);
    cumulativeCashFlow = round7(cumulativeCashFlow + cashFlow, 2);
    const date = new Date(baseDate);
    date.setMonth(date.getMonth() + month - 1);
    cashflows.push({
      month,
      year,
      date,
      grossRent: monthlyRent,
      otherIncome: monthlyOtherIncome,
      vacancyLoss,
      effectiveGrossIncome,
      operatingExpenses,
      noi,
      debtService,
      cashFlow,
      cumulativeCashFlow
    });
  }
  return cashflows;
}
function generateAnnualCashflows(input, monthlyCashflows, amortizationSchedule, _loanAmount) {
  const years = input.appreciationExit.holdingPeriodYears;
  const annualCashflows = [];
  const appreciationRate = fromPercent4(input.appreciationExit.annualAppreciation);
  const afterRepairValue = input.purchase.afterRepairValue || input.purchase.purchasePrice;
  for (let year = 1; year <= years; year++) {
    const startMonth = (year - 1) * 12;
    const endMonth = year * 12;
    const yearlyMonths = monthlyCashflows.slice(startMonth, endMonth);
    const grossRent = round7(
      yearlyMonths.reduce((sum, m) => sum + m.grossRent, 0),
      2
    );
    const otherIncome = round7(
      yearlyMonths.reduce((sum, m) => sum + m.otherIncome, 0),
      2
    );
    const vacancyLoss = round7(
      yearlyMonths.reduce((sum, m) => sum + m.vacancyLoss, 0),
      2
    );
    const effectiveGrossIncome = round7(
      yearlyMonths.reduce((sum, m) => sum + m.effectiveGrossIncome, 0),
      2
    );
    const operatingExpenses = round7(
      yearlyMonths.reduce((sum, m) => sum + m.operatingExpenses, 0),
      2
    );
    const noi = round7(
      yearlyMonths.reduce((sum, m) => sum + m.noi, 0),
      2
    );
    const debtService = round7(
      yearlyMonths.reduce((sum, m) => sum + m.debtService, 0),
      2
    );
    const cashFlow = round7(
      yearlyMonths.reduce((sum, m) => sum + m.cashFlow, 0),
      2
    );
    const cumulativeCashFlow = yearlyMonths[yearlyMonths.length - 1]?.cumulativeCashFlow || 0;
    const propertyValue = round7(
      compoundGrowth(afterRepairValue, appreciationRate, year),
      2
    );
    const loanBalanceMonth = year * 12;
    const loanBalance = loanBalanceMonth <= amortizationSchedule.length ? amortizationSchedule[loanBalanceMonth - 1].balance : 0;
    const equity = round7(propertyValue - loanBalance, 2);
    let principalPaid = 0;
    let interestPaid = 0;
    for (let month = startMonth + 1; month <= endMonth && month <= amortizationSchedule.length; month++) {
      principalPaid += amortizationSchedule[month - 1].principal;
      interestPaid += amortizationSchedule[month - 1].interest;
    }
    annualCashflows.push({
      year,
      grossRent,
      otherIncome,
      vacancyLoss,
      effectiveGrossIncome,
      operatingExpenses,
      noi,
      debtService,
      cashFlow,
      cumulativeCashFlow,
      propertyValue,
      loanBalance,
      equity,
      principalPaid: round7(principalPaid, 2),
      interestPaid: round7(interestPaid, 2)
    });
  }
  return annualCashflows;
}

// src/calculations/projections/equity.ts
import { round as round8, fromPercent as fromPercent5 } from "@roi/utils";
function generateEquityTimeline(input, loanAmount, totalCashRequired, amortizationSchedule) {
  const totalMonths = input.appreciationExit.holdingPeriodYears * 12;
  const appreciationRate = fromPercent5(input.appreciationExit.annualAppreciation);
  const timeline = [];
  const initialEquity = totalCashRequired;
  const afterRepairValue = input.purchase.afterRepairValue || input.purchase.purchasePrice;
  const initialPropertyValue = afterRepairValue;
  const totalCost = input.purchase.purchasePrice + (input.purchase.rehabCosts || 0);
  const instantEquity = afterRepairValue - totalCost;
  for (let month = 1; month <= totalMonths; month++) {
    const year = Math.ceil(month / 12);
    const monthlyAppreciationRate = Math.pow(1 + appreciationRate, 1 / 12) - 1;
    const propertyValue = round8(
      initialPropertyValue * Math.pow(1 + monthlyAppreciationRate, month),
      2
    );
    const loanBalance = month <= amortizationSchedule.length ? amortizationSchedule[month - 1].balance : 0;
    const equity = round8(propertyValue - loanBalance, 2);
    const marketAppreciation = propertyValue - initialPropertyValue;
    const appreciationEquity = round8(instantEquity + marketAppreciation, 2);
    const principalEquity = round8(loanAmount - loanBalance, 2);
    timeline.push({
      month,
      year,
      propertyValue,
      loanBalance,
      equity,
      appreciationEquity,
      principalEquity,
      initialEquity
    });
  }
  return timeline;
}
function calculateSaleProceeds(input, propertyValueAtSale, loanBalanceAtSale) {
  const grossSalePrice = input.appreciationExit.salePriceOverride || propertyValueAtSale;
  const sellingCosts = round8(grossSalePrice * fromPercent5(input.appreciationExit.sellingCostsPercent), 2);
  const netSaleProceeds = round8(grossSalePrice - sellingCosts, 2);
  const netProceeds = round8(netSaleProceeds - loanBalanceAtSale, 2);
  return {
    grossSalePrice,
    sellingCosts,
    netSaleProceeds,
    loanPayoff: loanBalanceAtSale,
    netProceeds
  };
}

// src/calculations/tax/depreciation.ts
function calculateDepreciation(input) {
  const {
    purchasePrice,
    closingCosts,
    rehabCosts,
    landValuePercent,
    depreciationYears
  } = input;
  const validDepreciationYears = depreciationYears >= 1 && depreciationYears <= 50 ? depreciationYears : 27.5;
  const totalCostBasis = purchasePrice + closingCosts + rehabCosts;
  const landValue = purchasePrice * (landValuePercent / 100);
  const depreciableBasis = totalCostBasis - landValue;
  const annualDepreciation = depreciableBasis / validDepreciationYears;
  const schedule = [];
  let accumulatedDepreciation = 0;
  for (let year = 1; year <= Math.ceil(validDepreciationYears); year++) {
    const isPartialYear = year > Math.floor(validDepreciationYears);
    const yearDepreciation = isPartialYear ? annualDepreciation * (validDepreciationYears - Math.floor(validDepreciationYears)) : annualDepreciation;
    accumulatedDepreciation += yearDepreciation;
    schedule.push({
      year,
      annualDepreciation: Math.round(yearDepreciation * 100) / 100,
      accumulatedDepreciation: Math.round(accumulatedDepreciation * 100) / 100,
      remainingBasis: Math.max(0, Math.round((depreciableBasis - accumulatedDepreciation) * 100) / 100)
    });
    if (accumulatedDepreciation >= depreciableBasis) break;
  }
  return {
    totalCostBasis,
    landValue,
    depreciableBasis,
    annualDepreciation: Math.round(annualDepreciation * 100) / 100,
    depreciationYears: validDepreciationYears,
    schedule
  };
}
function getDepreciationForYear(result, year) {
  const entry = result.schedule.find((e) => e.year === year);
  return entry?.annualDepreciation ?? 0;
}
function getAccumulatedDepreciation(result, year) {
  const entry = result.schedule.find((e) => e.year === year);
  return entry?.accumulatedDepreciation ?? 0;
}
function calculateDepreciationRecapture(accumulatedDepreciation, recaptureRate = 25) {
  return accumulatedDepreciation * (recaptureRate / 100);
}

// src/calculations/tax/tax-shield.ts
function calculateTaxShield(input) {
  const {
    mortgageInterest,
    depreciation,
    operatingExpenses,
    marginalTaxRate,
    stateTaxRate = 0
  } = input;
  const totalDeductions = mortgageInterest + depreciation + operatingExpenses;
  const federalTaxSavings = totalDeductions * marginalTaxRate;
  const stateTaxSavings = totalDeductions * stateTaxRate;
  const totalTaxSavings = federalTaxSavings + stateTaxSavings;
  const effectiveTaxRate = marginalTaxRate + stateTaxRate;
  return {
    totalDeductions,
    federalTaxSavings: Math.round(federalTaxSavings * 100) / 100,
    stateTaxSavings: Math.round(stateTaxSavings * 100) / 100,
    totalTaxSavings: Math.round(totalTaxSavings * 100) / 100,
    effectiveTaxRate,
    deductionBreakdown: {
      mortgageInterest,
      depreciation,
      operatingExpenses
    }
  };
}
function calculateAfterTaxCashFlow(input) {
  const {
    preTaxCashFlow,
    taxableIncome,
    marginalTaxRate,
    stateTaxRate = 0
  } = input;
  const combinedTaxRate = marginalTaxRate + stateTaxRate;
  const taxLiability = taxableIncome * combinedTaxRate;
  const taxSavingsFromLoss = taxableIncome < 0 ? Math.abs(taxLiability) : 0;
  const afterTaxCashFlow = preTaxCashFlow - taxLiability;
  return {
    preTaxCashFlow,
    taxableIncome,
    taxLiability: Math.round(taxLiability * 100) / 100,
    afterTaxCashFlow: Math.round(afterTaxCashFlow * 100) / 100,
    taxSavingsFromLoss: Math.round(taxSavingsFromLoss * 100) / 100
  };
}
function calculateTaxableIncome(noi, mortgageInterest, depreciation) {
  return noi - mortgageInterest - depreciation;
}
function calculateCapitalGainsTax(input) {
  const {
    salePrice,
    costBasis,
    sellingCosts,
    accumulatedDepreciation,
    capitalGainsRate,
    depreciationRecaptureRate = 0.25,
    holdingPeriodYears,
    ordinaryIncomeRate = capitalGainsRate
  } = input;
  const isLongTerm = holdingPeriodYears > 1;
  const netSaleProceeds = salePrice - sellingCosts;
  const adjustedCostBasis = costBasis - accumulatedDepreciation;
  const totalGain = netSaleProceeds - adjustedCostBasis;
  if (totalGain <= 0) {
    return {
      netSaleProceeds,
      adjustedCostBasis,
      totalGain,
      depreciationRecapture: 0,
      capitalGain: totalGain,
      recaptureTax: 0,
      capitalGainsTax: 0,
      totalTaxOnSale: 0,
      afterTaxProceeds: netSaleProceeds,
      isLongTerm
    };
  }
  const depreciationRecapture = Math.min(accumulatedDepreciation, totalGain);
  const capitalGain = Math.max(0, totalGain - depreciationRecapture);
  const effectiveRecaptureRate = Math.min(depreciationRecaptureRate, ordinaryIncomeRate);
  const recaptureTax = depreciationRecapture * effectiveRecaptureRate;
  const effectiveCapGainsRate = isLongTerm ? capitalGainsRate : ordinaryIncomeRate;
  const capitalGainsTax = capitalGain * effectiveCapGainsRate;
  const totalTaxOnSale = recaptureTax + capitalGainsTax;
  const afterTaxProceeds = netSaleProceeds - totalTaxOnSale;
  return {
    netSaleProceeds: Math.round(netSaleProceeds * 100) / 100,
    adjustedCostBasis: Math.round(adjustedCostBasis * 100) / 100,
    totalGain: Math.round(totalGain * 100) / 100,
    depreciationRecapture: Math.round(depreciationRecapture * 100) / 100,
    capitalGain: Math.round(capitalGain * 100) / 100,
    recaptureTax: Math.round(recaptureTax * 100) / 100,
    capitalGainsTax: Math.round(capitalGainsTax * 100) / 100,
    totalTaxOnSale: Math.round(totalTaxOnSale * 100) / 100,
    afterTaxProceeds: Math.round(afterTaxProceeds * 100) / 100,
    isLongTerm
  };
}

// src/pipeline/analysis-pipeline.ts
import { round as round9 } from "@roi/utils";

// src/utils/memoize.ts
var LRUCache = class {
  cache;
  maxSize;
  constructor(maxSize = 100) {
    this.cache = /* @__PURE__ */ new Map();
    this.maxSize = maxSize;
  }
  get(key) {
    const value = this.cache.get(key);
    if (value !== void 0) {
      this.cache.delete(key);
      this.cache.set(key, value);
    }
    return value;
  }
  set(key, value) {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== void 0) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(key, value);
  }
  has(key) {
    return this.cache.has(key);
  }
  clear() {
    this.cache.clear();
  }
  get size() {
    return this.cache.size;
  }
};
function hashObject(obj) {
  return JSON.stringify(obj, Object.keys(obj).sort());
}
function memoize(fn, options = {}) {
  const { maxSize = 100, keyGenerator = (...args) => hashObject(args) } = options;
  const cache = new LRUCache(maxSize);
  const memoized = (...args) => {
    const key = keyGenerator(...args);
    const cached = cache.get(key);
    if (cached !== void 0) {
      return cached;
    }
    const result = fn(...args);
    cache.set(key, result);
    return result;
  };
  memoized.cache = cache;
  memoized.clearCache = () => cache.clear();
  return memoized;
}

// src/pipeline/warnings.ts
function evaluateWarnings(input, metrics) {
  const warnings = [];
  if (metrics.dscr < 1 && metrics.dscr !== Infinity) {
    warnings.push({
      code: "LOW_DSCR",
      severity: "error",
      message: `DSCR of ${metrics.dscr.toFixed(2)} is below 1.0, meaning NOI doesn't cover debt service. This property has negative cash flow.`,
      field: "financing",
      suggestion: "Consider a larger down payment or lower purchase price to improve cash flow."
    });
  } else if (metrics.dscr < 1.25 && metrics.dscr !== Infinity) {
    warnings.push({
      code: "MARGINAL_DSCR",
      severity: "warning",
      message: `DSCR of ${metrics.dscr.toFixed(2)} is below the typical lender minimum of 1.25.`,
      field: "financing",
      suggestion: "Many lenders require DSCR of 1.25+. Consider improving income or reducing loan amount."
    });
  }
  if (metrics.annualCashFlow < 0) {
    warnings.push({
      code: "NEGATIVE_CASH_FLOW",
      severity: "warning",
      message: `Annual cash flow is negative ($${Math.abs(metrics.annualCashFlow).toLocaleString()}). You will need to contribute additional funds each year.`,
      field: "income"
    });
  }
  if (input.income.vacancyRate > 10) {
    warnings.push({
      code: "HIGH_VACANCY",
      severity: "info",
      message: `Vacancy rate of ${input.income.vacancyRate}% is higher than typical (5-8%). Verify this is appropriate for your market.`,
      field: "income.vacancyRate"
    });
  }
  if (input.income.vacancyRate < 3 && input.income.monthlyRent > 0) {
    warnings.push({
      code: "LOW_VACANCY",
      severity: "info",
      message: `Vacancy rate of ${input.income.vacancyRate}% may be optimistic. Consider using 5% or higher for conservative projections.`,
      field: "income.vacancyRate"
    });
  }
  if (metrics.oer > 0.6) {
    warnings.push({
      code: "HIGH_OER",
      severity: "warning",
      message: `Operating expense ratio of ${(metrics.oer * 100).toFixed(1)}% is high. Typical range is 35-50%.`,
      field: "expenses",
      suggestion: "Review expenses or verify income assumptions."
    });
  }
  if (metrics.capRate > 0 && metrics.capRate < 0.04) {
    warnings.push({
      code: "LOW_CAP_RATE",
      severity: "info",
      message: `Cap rate of ${(metrics.capRate * 100).toFixed(2)}% is low. This indicates a premium price relative to income.`,
      field: "purchase.purchasePrice"
    });
  }
  if (input.financing.interestRate > 10) {
    warnings.push({
      code: "HIGH_INTEREST_RATE",
      severity: "warning",
      message: `Interest rate of ${input.financing.interestRate}% is high. Consider shopping for better financing options.`,
      field: "financing.interestRate"
    });
  }
  if (input.financing.downPaymentPercent < 20 && input.financing.loanType === "conventional") {
    warnings.push({
      code: "LOW_DOWN_PAYMENT",
      severity: "info",
      message: `Down payment of ${input.financing.downPaymentPercent}% may require PMI for conventional loans.`,
      field: "financing.downPaymentPercent"
    });
  }
  if (input.appreciationExit.annualAppreciation > 7) {
    warnings.push({
      code: "HIGH_APPRECIATION",
      severity: "warning",
      message: `Annual appreciation of ${input.appreciationExit.annualAppreciation}% is above historical averages (3-4%). Consider using more conservative estimates.`,
      field: "appreciationExit.annualAppreciation"
    });
  }
  if (input.appreciationExit.holdingPeriodYears > 15) {
    warnings.push({
      code: "LONG_HOLD",
      severity: "info",
      message: `${input.appreciationExit.holdingPeriodYears}-year holding period is long. Projections become less reliable over extended periods.`,
      field: "appreciationExit.holdingPeriodYears"
    });
  }
  if (input.income.monthlyRent === 0) {
    warnings.push({
      code: "NO_RENT",
      severity: "error",
      message: "Monthly rent is $0. Please enter the expected rental income.",
      field: "income.monthlyRent"
    });
  }
  const totalExpenses = metrics.totalOperatingExpenses;
  if (totalExpenses === 0 && input.income.monthlyRent > 0) {
    warnings.push({
      code: "NO_EXPENSES",
      severity: "warning",
      message: "Operating expenses are $0. All properties have some expenses (taxes, insurance, maintenance).",
      field: "expenses",
      suggestion: "Add property tax, insurance, and maintenance estimates."
    });
  }
  if (metrics.afterTaxCashOnCash !== void 0) {
    const taxBenefit = metrics.afterTaxCashOnCash - metrics.cashOnCash;
    if (Math.abs(taxBenefit) > 0.15) {
      warnings.push({
        code: "UNREALISTIC_TAX_BENEFIT",
        severity: "error",
        message: `Tax benefit of ${(taxBenefit * 100).toFixed(1)}% seems unrealistic. Please verify tax settings.`,
        field: "taxes",
        suggestion: "Check depreciation period (should be 27.5 years for residential, 39 for commercial)."
      });
    }
  }
  return warnings;
}

// src/pipeline/analysis-pipeline.ts
var DEFAULT_DISCOUNT_RATE = 0.08;
function analyze(input, options = {}) {
  const startTime = performance.now();
  const { startDate = /* @__PURE__ */ new Date() } = options;
  const validatedInput = DealInputSchema.parse(input);
  const loanAmount = calculateLoanAmount(validatedInput);
  const totalCashRequired = calculateTotalCashRequired(validatedInput);
  const amortization = generateAmortizationSchedule(validatedInput, loanAmount, startDate);
  const incomeBreakdown = calculateIncomeBreakdown(validatedInput);
  const expenseBreakdown = calculateExpenseBreakdown(validatedInput, incomeBreakdown.effectiveGrossIncome);
  const noi = calculateNOI(incomeBreakdown.effectiveGrossIncome, expenseBreakdown.total);
  const oer = calculateOER(expenseBreakdown.total, incomeBreakdown.effectiveGrossIncome);
  const capRate = calculateCapRate(noi, validatedInput.purchase.purchasePrice);
  const grm = calculateGRM(
    validatedInput.purchase.purchasePrice,
    incomeBreakdown.grossRent + incomeBreakdown.otherIncome
  );
  const annualDebtService = amortization.monthlyPayment * 12;
  const dscr = calculateDSCR(noi, annualDebtService);
  const annualCashFlow = round9(noi - annualDebtService, 2);
  const monthlyCashFlow = round9(annualCashFlow / 12, 2);
  const cashOnCash = calculateCashOnCash(annualCashFlow, totalCashRequired);
  const monthlyCashflows = generateMonthlyCashflows(
    validatedInput,
    incomeBreakdown,
    expenseBreakdown,
    amortization.schedule,
    startDate
  );
  const annualCashflows = generateAnnualCashflows(
    validatedInput,
    monthlyCashflows,
    amortization.schedule,
    loanAmount
  );
  const equityTimeline = generateEquityTimeline(
    validatedInput,
    loanAmount,
    totalCashRequired,
    amortization.schedule
  );
  const discountRate = options.discountRate ?? DEFAULT_DISCOUNT_RATE;
  const holdingPeriod = validatedInput.appreciationExit.holdingPeriodYears;
  const finalEquity = equityTimeline[equityTimeline.length - 1];
  const saleProceeds = calculateSaleProceeds(
    validatedInput,
    finalEquity?.propertyValue || validatedInput.purchase.purchasePrice,
    finalEquity?.loanBalance || 0
  );
  const annualCashflowValues = annualCashflows.map((y) => y.cashFlow);
  const annualNOIValues = annualCashflows.map((y) => y.noi);
  const irrLeveraged = calculateLeveragedIRR(
    totalCashRequired,
    annualCashflowValues,
    saleProceeds.netProceeds
  );
  const irrUnleveraged = calculateUnleveragedIRR(
    validatedInput.purchase.purchasePrice,
    annualNOIValues,
    saleProceeds.grossSalePrice,
    saleProceeds.sellingCosts
  );
  const npvCashflows = [-totalCashRequired, ...annualCashflowValues];
  npvCashflows[npvCashflows.length - 1] += saleProceeds.netProceeds;
  const npv = calculateInvestmentNPV(
    totalCashRequired,
    annualCashflowValues,
    saleProceeds.netProceeds,
    discountRate
  );
  const profitabilityIndex = calculateProfitabilityIndex(npvCashflows, discountRate);
  const equityMultiple = calculateEquityMultipleFromCashflows(
    totalCashRequired,
    annualCashflowValues,
    saleProceeds.netProceeds
  );
  const totalReturnDollars = calculateTotalReturn(
    totalCashRequired,
    annualCashflowValues,
    saleProceeds.netProceeds
  );
  const totalReturnPercent = calculateTotalReturnPercent(totalCashRequired, totalReturnDollars);
  const averageAnnualReturn = calculateAverageAnnualReturn(totalReturnPercent, holdingPeriod);
  const grossPotentialRent = incomeBreakdown.grossRent + incomeBreakdown.otherIncome;
  const breakEvenOccupancy = calculateBreakEvenOccupancy(
    grossPotentialRent,
    expenseBreakdown.total,
    annualDebtService
  );
  const paybackPeriodYears = calculatePaybackPeriod(totalCashRequired, annualCashflowValues);
  const rentToPrice = calculateRentToPrice(
    validatedInput.income.monthlyRent,
    validatedInput.purchase.purchasePrice
  );
  let afterTaxCashOnCash;
  let afterTaxIrr;
  const taxes = validatedInput.taxes;
  if (taxes && taxes.depreciationEnabled && taxes.marginalTaxRate !== void 0) {
    const depreciationResult = calculateDepreciation({
      purchasePrice: validatedInput.purchase.purchasePrice,
      closingCosts: validatedInput.purchase.closingCosts,
      rehabCosts: validatedInput.purchase.rehabCosts,
      landValuePercent: taxes.landValuePercent ?? 20,
      depreciationYears: taxes.depreciationYears ?? 27.5
    });
    const marginalRate = (taxes.marginalTaxRate ?? 0) / 100;
    const stateRate = (taxes.stateTaxRate ?? 0) / 100;
    const year1Interest = annualCashflows[0]?.interestPaid ?? 0;
    const year1Depreciation = getDepreciationForYear(depreciationResult, 1);
    const taxableIncome = calculateTaxableIncome(noi, year1Interest, year1Depreciation);
    const afterTaxResult = calculateAfterTaxCashFlow({
      preTaxCashFlow: annualCashFlow,
      taxableIncome,
      marginalTaxRate: marginalRate,
      stateTaxRate: stateRate
    });
    afterTaxCashOnCash = round9(
      afterTaxResult.afterTaxCashFlow / totalCashRequired,
      4
    );
    const afterTaxCashflows = [];
    for (let i = 0; i < annualCashflows.length; i++) {
      const yearNum = i + 1;
      const yearInterest = annualCashflows[i]?.interestPaid ?? 0;
      const yearDepreciation = getDepreciationForYear(depreciationResult, yearNum);
      const yearNOI = annualCashflows[i]?.noi ?? 0;
      const yearCashFlow = annualCashflows[i]?.cashFlow ?? 0;
      const yearTaxableIncome = calculateTaxableIncome(yearNOI, yearInterest, yearDepreciation);
      const yearAfterTax = calculateAfterTaxCashFlow({
        preTaxCashFlow: yearCashFlow,
        taxableIncome: yearTaxableIncome,
        marginalTaxRate: marginalRate,
        stateTaxRate: stateRate
      });
      afterTaxCashflows.push(yearAfterTax.afterTaxCashFlow);
    }
    const accumulatedDep = getAccumulatedDepreciation(depreciationResult, holdingPeriod);
    const capitalGainsRate = (taxes.capitalGainsTaxRate ?? 15) / 100;
    const depreciationRecaptureRate = 0.25;
    const adjustedBasis = totalCashRequired - accumulatedDep;
    const totalGain = Math.max(0, saleProceeds.netProceeds - adjustedBasis);
    const depRecapture = Math.min(accumulatedDep, totalGain);
    const capitalGain = Math.max(0, totalGain - depRecapture);
    const saleTax = depRecapture * depreciationRecaptureRate + capitalGain * capitalGainsRate;
    const afterTaxSaleProceeds = saleProceeds.netProceeds - saleTax;
    const irr = calculateLeveragedIRR(
      totalCashRequired,
      afterTaxCashflows,
      afterTaxSaleProceeds
    );
    afterTaxIrr = irr ?? void 0;
  }
  const summaryMetrics = {
    // Income metrics
    grossPotentialIncome: grossPotentialRent,
    effectiveGrossIncome: incomeBreakdown.effectiveGrossIncome,
    totalOperatingExpenses: expenseBreakdown.total,
    noi,
    oer,
    // Purchase metrics
    purchasePrice: validatedInput.purchase.purchasePrice,
    totalCashRequired,
    loanAmount,
    monthlyDebtService: amortization.monthlyPayment,
    annualDebtService,
    // Return metrics
    capRate,
    cashOnCash,
    grm,
    dscr,
    // Cash flow
    monthlyCashFlow,
    annualCashFlow,
    // Advanced return metrics
    irrLeveraged,
    irrUnleveraged,
    npv,
    npvDiscountRate: discountRate,
    equityMultiple,
    totalReturn: totalReturnDollars,
    totalReturnPercent,
    averageAnnualReturn,
    breakEvenOccupancy,
    paybackPeriodYears,
    rentToPrice,
    profitabilityIndex,
    // Tax-aware metrics (when enabled)
    afterTaxCashOnCash,
    afterTaxIrr
  };
  const warnings = evaluateWarnings(validatedInput, summaryMetrics);
  const computeTimeMs = performance.now() - startTime;
  return {
    summaryMetrics,
    monthlyCashflows,
    annualCashflows,
    amortizationSchedule: amortization.schedule,
    equityTimeline,
    expenseBreakdown,
    incomeBreakdown,
    warnings,
    explainability: [],
    // To be implemented
    computedAt: /* @__PURE__ */ new Date(),
    computeTimeMs: round9(computeTimeMs, 2)
  };
}
function analyzeWithScenario(baseInput, overrides, options = {}) {
  const mergedInput = deepMerge(baseInput, overrides);
  return analyze(mergedInput, options);
}
function deepMerge(base, overrides) {
  const result = { ...base };
  for (const key in overrides) {
    const baseValue = base[key];
    const overrideValue = overrides[key];
    if (overrideValue !== void 0 && typeof overrideValue === "object" && overrideValue !== null && !Array.isArray(overrideValue) && typeof baseValue === "object" && baseValue !== null && !Array.isArray(baseValue)) {
      result[key] = deepMerge(
        baseValue,
        overrideValue
      );
    } else if (overrideValue !== void 0) {
      result[key] = overrideValue;
    }
  }
  return result;
}
var analyzeMemoized = memoize(
  (input, options = {}) => analyze(input, options),
  {
    maxSize: 50,
    // Cache up to 50 analyses
    keyGenerator: (input, options = {}) => {
      const { startDate, ...cacheableOptions } = options;
      return hashObject({ input, options: cacheableOptions });
    }
  }
);
function clearAnalysisCache() {
  analyzeMemoized.clearCache();
}
function getAnalysisCacheSize() {
  return analyzeMemoized.cache.size;
}

// src/scenarios/presets.ts
var SCENARIO_PRESETS = {
  base: {
    name: "Base Case",
    description: "Current assumptions with no modifications",
    preset: "base",
    overrides: {},
    color: "#3b82f6"
    // Blue
  },
  optimistic: {
    name: "Optimistic",
    description: "Higher rent growth, lower vacancy, stronger appreciation",
    preset: "optimistic",
    overrides: {
      income: {
        vacancyRate: 3,
        annualRentGrowth: 4
      },
      appreciationExit: {
        annualAppreciation: 4
      }
    },
    color: "#22c55e"
    // Green
  },
  conservative: {
    name: "Conservative",
    description: "Lower rent growth, higher vacancy, modest appreciation",
    preset: "conservative",
    overrides: {
      income: {
        vacancyRate: 8,
        annualRentGrowth: 2
      },
      appreciationExit: {
        annualAppreciation: 2
      }
    },
    color: "#f97316"
    // Orange
  },
  "best-case": {
    name: "Best Case",
    description: "Ideal conditions - maximum rent, minimal vacancy, strong growth",
    preset: "best-case",
    overrides: {
      income: {
        vacancyRate: 2,
        annualRentGrowth: 5
      },
      expenses: {
        maintenancePercent: 3
      },
      appreciationExit: {
        annualAppreciation: 5
      }
    },
    color: "#10b981"
    // Emerald
  },
  "worst-case": {
    name: "Worst Case",
    description: "Challenging conditions - high vacancy, no growth, flat appreciation",
    preset: "worst-case",
    overrides: {
      income: {
        vacancyRate: 15,
        annualRentGrowth: 0
      },
      expenses: {
        maintenancePercent: 10
      },
      appreciationExit: {
        annualAppreciation: 0
      }
    },
    color: "#ef4444"
    // Red
  },
  custom: {
    name: "Custom",
    description: "User-defined scenario",
    preset: "custom",
    overrides: {},
    color: "#8b5cf6"
    // Purple
  }
};
function createScenarioConfigFromPreset(preset, customOverrides) {
  const presetConfig = SCENARIO_PRESETS[preset];
  const id = `scenario-${preset}-${Date.now()}`;
  return {
    id,
    ...presetConfig,
    overrides: customOverrides ?? presetConfig.overrides
  };
}
function createCustomScenarioConfig(name, description, overrides, color = "#8b5cf6") {
  return {
    id: `scenario-custom-${Date.now()}`,
    name,
    description,
    preset: "custom",
    overrides,
    color
  };
}
function getAllPresetConfigs() {
  return Object.keys(SCENARIO_PRESETS).map(
    (key) => createScenarioConfigFromPreset(key)
  );
}
function getDefaultScenarioConfigs() {
  return [
    createScenarioConfigFromPreset("base"),
    createScenarioConfigFromPreset("optimistic"),
    createScenarioConfigFromPreset("conservative")
  ];
}

// src/scenarios/engine.ts
function applyScenarioOverrides(base, overrides) {
  return {
    ...base,
    purchase: {
      ...base.purchase,
      ...overrides.purchase ?? {}
    },
    financing: {
      ...base.financing,
      ...overrides.financing ?? {}
    },
    income: {
      ...base.income,
      ...overrides.income ?? {}
    },
    expenses: {
      ...base.expenses,
      ...overrides.expenses ?? {}
    },
    appreciationExit: {
      ...base.appreciationExit,
      ...overrides.appreciationExit ?? {}
    }
  };
}
function analyzeScenarioConfig(baseDealInput, scenario) {
  const mergedInput = applyScenarioOverrides(baseDealInput, scenario.overrides);
  const result = analyze(mergedInput);
  return {
    scenario,
    dealInput: mergedInput,
    result
  };
}
function compareScenarioConfigs(baseDealInput, scenarios) {
  const hasBase = scenarios.some((s) => s.preset === "base");
  const allScenarios = hasBase ? scenarios : [createScenarioConfigFromPreset("base"), ...scenarios];
  const results = allScenarios.map(
    (scenario) => analyzeScenarioConfig(baseDealInput, scenario)
  );
  const base = results.find((r) => r.scenario.preset === "base") ?? results[0];
  const metricComparison = buildMetricComparisons(results, base);
  return {
    base,
    scenarios: results,
    metricComparison
  };
}
function buildMetricComparisons(results, base) {
  const metrics = [
    {
      name: "cashOnCash",
      label: "Cash-on-Cash Return",
      format: "percent",
      getValue: (r) => r.summaryMetrics.cashOnCash * 100
    },
    {
      name: "capRate",
      label: "Cap Rate",
      format: "percent",
      getValue: (r) => r.summaryMetrics.capRate * 100
    },
    {
      name: "noi",
      label: "Year 1 NOI",
      format: "currency",
      getValue: (r) => r.summaryMetrics.noi
    },
    {
      name: "annualCashFlow",
      label: "Year 1 Cash Flow",
      format: "currency",
      getValue: (r) => r.annualCashflows[0]?.cashFlow ?? 0
    },
    {
      name: "dscr",
      label: "Debt Service Coverage",
      format: "number",
      getValue: (r) => r.summaryMetrics.dscr
    },
    {
      name: "irr",
      label: "Leveraged IRR",
      format: "percent",
      getValue: (r) => (r.summaryMetrics.irrLeveraged ?? 0) * 100
    },
    {
      name: "totalReturn",
      label: "Total Return",
      format: "percent",
      getValue: (r) => r.summaryMetrics.totalReturnPercent ?? 0
    },
    {
      name: "equityMultiple",
      label: "Equity Multiple",
      format: "number",
      getValue: (r) => r.summaryMetrics.equityMultiple ?? 0
    },
    {
      name: "finalEquity",
      label: "Final Equity",
      format: "currency",
      getValue: (r) => {
        const lastCashflow = r.annualCashflows[r.annualCashflows.length - 1];
        return lastCashflow?.equity ?? 0;
      }
    }
  ];
  return metrics.map((metric) => {
    const baseValue = metric.getValue(base.result);
    return {
      metricName: metric.name,
      metricLabel: metric.label,
      format: metric.format,
      values: results.map((r) => {
        const value = metric.getValue(r.result);
        const delta = value - baseValue;
        const deltaPercent = baseValue !== 0 ? delta / Math.abs(baseValue) * 100 : 0;
        return {
          scenarioId: r.scenario.id,
          scenarioName: r.scenario.name,
          value,
          delta: r.scenario.preset === "base" ? void 0 : delta,
          deltaPercent: r.scenario.preset === "base" ? void 0 : deltaPercent
        };
      })
    };
  });
}
function calculateSensitivityRange(baseDealInput, variablePath, values) {
  return values.map((value) => {
    const override = createOverrideFromPath(variablePath, value);
    const mergedInput = applyScenarioOverrides(baseDealInput, override);
    const result = analyze(mergedInput);
    return { value, result };
  });
}
function createOverrideFromPath(path, value) {
  const parts = path.split(".");
  const override = {};
  if (parts.length !== 2) return override;
  const [section, field] = parts;
  switch (section) {
    case "purchase":
      override.purchase = { [field]: value };
      break;
    case "financing":
      override.financing = { [field]: value };
      break;
    case "income":
      override.income = { [field]: value };
      break;
    case "expenses":
      override.expenses = { [field]: value };
      break;
    case "appreciationExit":
      override.appreciationExit = { [field]: value };
      break;
  }
  return override;
}
export {
  AddressSchema,
  AmortizationEntrySchema,
  AnalysisResultSchema,
  AnnualCashflowSchema,
  AppreciationExitSchema,
  CurrencySchema,
  DealInputSchema,
  EquityTimelineEntrySchema,
  ExpenseBreakdownSchema,
  ExpensesSchema,
  ExplainabilityStepSchema,
  FinancingSchema,
  IncomeBreakdownSchema,
  IncomeSchema,
  LRUCache,
  LoanTypeSchema,
  MonthlyCashflowSchema,
  PropertyInfoSchema,
  PropertyRecordSchema,
  PurchaseSchema,
  SCENARIO_PRESETS,
  ScenarioSchema,
  ScenarioTemplates,
  SummaryMetricsSchema,
  TaxSettingsSchema,
  WarningSchema,
  WarningSeveritySchema,
  analyze,
  analyzeMemoized,
  analyzeScenarioConfig,
  analyzeWithScenario,
  applyScenarioOverrides,
  calculateAfterTaxCashFlow,
  calculateAnnuityPV,
  calculateAverageAnnualReturn,
  calculateBreakEvenOccupancy,
  calculateBreakEvenRent,
  calculateCapRate,
  calculateCapitalGainsTax,
  calculateCashOnCash,
  calculateDSCR,
  calculateDepreciation,
  calculateDepreciationRecapture,
  calculateDiscountedPaybackPeriod,
  calculateEquityMultiple,
  calculateEquityMultipleFromCashflows,
  calculateExpenseBreakdown,
  calculateFutureValue,
  calculateGRM,
  calculateIRR,
  calculateIncomeBreakdown,
  calculateInvestmentNPV,
  calculateLeveragedIRR,
  calculateLoanAmount,
  calculateMIRR,
  calculateMaxPurchasePrice,
  calculateNOI,
  calculateNPV,
  calculateNPVForRate,
  calculateOER,
  calculatePaybackPeriod,
  calculatePresentValue,
  calculateProfitabilityIndex,
  calculateRentToPrice,
  calculateRequiredDiscountRate,
  calculateReturnOnEquity,
  calculateSaleProceeds,
  calculateSensitivityRange,
  calculateTaxShield,
  calculateTaxableIncome,
  calculateTotalCashRequired,
  calculateTotalReturn,
  calculateTotalReturnPercent,
  calculateUnleveragedIRR,
  clearAnalysisCache,
  compareScenarioConfigs,
  createCustomScenarioConfig,
  createDefaultDealInput,
  createPropertyRecord,
  createScenario,
  createScenarioConfigFromPreset,
  createScenarioFromTemplate,
  estimateExpensesFiftyPercent,
  evaluateWarnings,
  generateAmortizationSchedule,
  generateAnnualCashflows,
  generateEquityTimeline,
  generateMonthlyCashflows,
  getAccumulatedDepreciation,
  getAllPresetConfigs,
  getAnalysisCacheSize,
  getAnnualDebtBreakdown,
  getDefaultScenarioConfigs,
  getDepreciationForYear,
  getInterestPaidByMonth,
  getLoanBalanceAtMonth,
  getPrincipalPaidByMonth,
  hashObject,
  meetsOnePercentRule,
  memoize
};
//# sourceMappingURL=index.js.map